import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase/config';
import { getDiagnostic } from '../services/diagnosticService';
import { getUserData } from '../services/authService';
import { generatePDF } from '../services/pdfService';
import {
  DIAGNOSTIC_AXES,
  calculateAxisScore,
  calculateGlobalScore,
  classifyScore,
  getAxisProblems,
  generateRecommendations,
  generateExecutiveSummary
} from '../services/diagnosticAnalysisService';

/* ═══════════════════════════════════════════════════════════════════
   MODAL UPGRADE — composant isolé, monté uniquement si free clique
   sur télécharger. Affiche un comparatif Silver / Gold avec les
   features issues des clés de traduction existantes.
   ═══════════════════════════════════════════════════════════════════ */
function UpgradeModal({ onClose, onGoToPricing }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';

  // Fermer sur click dehors du card
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-modal-in">

        {/* ── Bande haut avec gradient ── */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-5 text-center relative">
          {/* X fermer coin supérieur droit */}
          <button
            onClick={onClose}
            className="absolute top-3 right-4 text-white text-opacity-70 hover:text-opacity-100 text-2xl leading-none"
          >
            ×
          </button>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white bg-opacity-20 mb-3">
            <span className="text-3xl">🔒</span>
          </div>
          <h2 className="text-xl font-bold text-white">
            {lang === 'fr' ? 'Téléchargement verrouillé' : 'Download locked'}
          </h2>
          <p className="text-white text-opacity-80 text-sm mt-1">
            {t('downloadReservedSilverGold')}
          </p>
        </div>

        {/* ── Corps : comparatif Silver vs Gold ── */}
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 gap-3 mb-5">

            {/* Silver */}
            <div className="border border-gray-200 rounded-xl p-4 flex flex-col">
              <div className="text-center mb-3">
                <div className="text-2xl mb-1">🥈</div>
                <p className="font-bold text-gray-800 text-base">{t('silverPlan')}</p>
                <p className="mt-1">
                  <span className="text-xl font-bold text-gray-700">29,99€</span>
                  <span className="text-xs text-gray-400">/mois</span>
                </p>
              </div>
              <ul className="text-left text-xs text-gray-600 space-y-2 flex-1">
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{lang === 'fr' ? '3 diagnostics / mois' : '3 diagnostics / month'}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{t('pdfDownload')}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{lang === 'fr' ? 'Historique illimité' : 'Unlimited history'}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-gray-300 mt-0.5 flex-shrink-0">✗</span>
                  <span className="text-gray-400">{t('libraryAccess')}</span>
                </li>
              </ul>
              <button
                onClick={onGoToPricing}
                className="mt-4 w-full border border-gray-300 text-gray-700 font-semibold py-2 rounded-lg text-sm hover:bg-gray-50 transition"
              >
                {t('chooseSilver')}
              </button>
            </div>

            {/* Gold — mise en avant */}
            <div className="border-2 border-yellow-400 bg-yellow-50 rounded-xl p-4 flex flex-col relative">
              {/* Badge "Populaire" */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-yellow-500 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                  {t('popular')}
                </span>
              </div>
              <div className="text-center mb-3 mt-1">
                <div className="text-2xl mb-1">🥇</div>
                <p className="font-bold text-yellow-700 text-base">{t('goldPlan')}</p>
                <p className="mt-1">
                  <span className="text-xl font-bold text-yellow-600">49,99€</span>
                  <span className="text-xs text-gray-400">/mois</span>
                </p>
              </div>
              <ul className="text-left text-xs text-gray-600 space-y-2 flex-1">
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{lang === 'fr' ? '7 diagnostics / mois' : '7 diagnostics / month'}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{t('pdfDownload')}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{lang === 'fr' ? 'Historique illimité' : 'Unlimited history'}</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span>{t('fullLibrary')}</span>
                </li>
              </ul>
              <button
                onClick={onGoToPricing}
                className="mt-4 w-full bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold py-2 rounded-lg text-sm hover:from-green-700 hover:to-blue-700 transition"
              >
                {t('upgradeGold')}
              </button>
            </div>
          </div>

          {/* Lien texte fermer */}
          <p className="text-center">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">
              {t('close')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   BANNER UPGRADE — bande en bas de page, visible uniquement pour free
   ═══════════════════════════════════════════════════════════════════ */
function UpgradeBanner({ onGoToPricing }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';

  return (
    <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center shadow-sm border border-blue-200 flex-shrink-0">
          <span className="text-xl">🔒</span>
        </div>
        <div>
          <p className="font-semibold text-gray-800">{t('downloadReservedSilverGold')}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {lang === 'fr'
              ? 'Silver à 29,99€/mois · Gold à 49,99€/mois'
              : 'Silver at $29.99/mo · Gold at $49.99/mo'}
          </p>
        </div>
      </div>
      <button
        onClick={onGoToPricing}
        className="bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold py-2.5 px-6 rounded-lg hover:from-green-700 hover:to-blue-700 transition whitespace-nowrap shadow-sm flex-shrink-0"
      >
        {lang === 'fr' ? 'Voir les offres →' : 'View plans →'}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   BOUTON TÉLÉCHARGEMENT — réutilisé en haut et en bas.
   Gère visuellement & comportementalement l'état free vs payant.
   ═══════════════════════════════════════════════════════════════════ */
function DownloadButton({ canDownload, onClick, className = '', children }) {
  const { t } = useTranslation();

  const baseClass = 'flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-200';
  const activeClass = 'btn-primary cursor-pointer hover:shadow-md';
  const blockedClass = 'bg-gray-200 text-gray-400 cursor-not-allowed border border-dashed border-gray-300 hover:border-gray-400';

  return (
    <button
      onClick={onClick}
      disabled={!canDownload}
      className={`${baseClass} ${canDownload ? activeClass : blockedClass} ${className}`}
      title={!canDownload ? t('downloadReservedSilverGold') : ''}
    >
      <span>📄</span>
      <span>{children}</span>
      {!canDownload && <span className="text-sm opacity-70">🔒</span>}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL — DiagnosticResult
   ═══════════════════════════════════════════════════════════════════ */
function DiagnosticResult() {
  const { t, i18n } = useTranslation();
  const { diagnosticId } = useParams();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [diagnostic, setDiagnostic] = useState(null);
  const [userData, setUserData] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [globalScore, setGlobalScore] = useState(0);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const lang = i18n.language || 'fr';

  // ── Autorisation unique source of truth ──
  const canDownload = userData?.subscriptionType === 'silver' || userData?.subscriptionType === 'gold';

  // ── Analyse des réponses du diagnostic ──
  const analyzeResponses = useCallback((responses) => {
    const results = [];
    let startIndex = 0;

    DIAGNOSTIC_AXES.forEach(axis => {
      const axisResponses = responses.slice(startIndex, startIndex + axis.questions.length);
      const score = calculateAxisScore(axisResponses);

      results.push({
        id: axis.id,
        name: lang === 'fr' ? axis.name : (axis.nameEn || axis.name),
        nameFr: axis.name,
        nameEn: axis.nameEn || axis.name,
        score,
        classification: classifyScore(score, lang),
        responses: axisResponses,
        problems: getAxisProblems(axis.id, score, lang)
      });

      startIndex += axis.questions.length;
    });

    setAnalysisResults(results);

    const axisScores = results.map(r => r.score);
    const global = calculateGlobalScore(axisScores);
    setGlobalScore(global);
    setExecutiveSummary(generateExecutiveSummary(global, results, lang));
    setRecommendations(generateRecommendations(results, lang));
  }, [lang]);

  // ── Chargement initial ──
  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    const load = async () => {
      const diagResult = await getDiagnostic(diagnosticId);
      if (!diagResult.success) {
        alert(t('diagnosticNotFound'));
        navigate('/dashboard');
        return;
      }
      if (diagResult.diagnostic.userId !== user.uid) {
        alert(t('accessNotAuthorized'));
        navigate('/dashboard');
        return;
      }

      setDiagnostic(diagResult.diagnostic);

      const userResult = await getUserData(user.uid);
      if (userResult.success) setUserData(userResult.data);

      analyzeResponses(diagResult.diagnostic.responses);
      setLoading(false);
    };

    load();
  }, [user, diagnosticId, navigate, t, analyzeResponses]);

  // ── Handler téléchargement ──
  const handleDownloadPDF = () => {
    if (!canDownload) {
      setShowUpgradeModal(true);
      return;
    }
    try {
      generatePDF(diagnostic, analysisResults, globalScore, executiveSummary, recommendations, lang, t);
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert(t('pdfGenerationError'));
    }
  };

  // ── Indicateurs de succès par axe (bilingue) ──
  const getSuccessIndicator = (axisName) => {
    const map = {
      fr: {
        'Vision': 'Objectifs SMART validés et communiqués',
        'Priorités': 'Planning stabilisé et respecté',
        'Gouvernance': 'Décisions prises en moins de 48h',
        'Réunions': 'Taux de satisfaction réunions > 80%',
        'Exécution': 'Écarts détectés sous 1 semaine',
        'Climat': 'Baisse du stress ressenti (enquête)'
      },
      en: {
        'Vision': 'SMART objectives validated and communicated',
        'Priorities': 'Stabilized and respected planning',
        'Governance': 'Decisions made in less than 48h',
        'Meetings': 'Meeting satisfaction rate > 80%',
        'Execution': 'Deviations detected within 1 week',
        'Climate': 'Decrease in perceived stress (survey)'
      }
    };
    const indicators = map[lang] || map.fr;
    for (const [key, value] of Object.entries(indicators)) {
      if (axisName.includes(key)) return value;
    }
    return '';
  };

  // ── Ressources recommandées par axe (bilingue, mutualisée) ──
  const getResources = (axisName) => {
    const n = (fr, en) => lang === 'fr' ? fr : en;
    const book = (fr, en, id) => ({ title: n(fr, en), bookId: id });

    const catalog = [
      { keys: ['vision', 'objectif'], items: [book("L'état d'esprit du leader", "The Leader's Mindset", 2), book("L'âme du projet", "Humans at the Heart of the Project", 3), book("Le modèle symphonique", "The Symphonic Model", 4)] },
      { keys: ['priorité', 'planif', 'priorit'], items: [book("La planification harmonieuse", "Harmonious Planning", 5), book("Le modèle symphonique", "The Symphonic Model", 4), book("L'état d'esprit du leader", "The Leader's Mindset", 2)] },
      { keys: ['gouvernance', 'décision', 'decision'], items: [book("Rythme, leadership et ennéagramme", "Rhythm, Leadership and Enneagram", 9), book("L'âme du projet", "Humans at the Heart of the Project", 3), book("Le modèle symphonique", "The Symphonic Model", 4)] },
      { keys: ['réunion', 'communication', 'meeting'], items: [book("L'âme du projet", "Humans at the Heart of the Project", 3), book("Rythme, leadership et ennéagramme", "Rhythm, Leadership and Enneagram", 9), book("L'état d'esprit du leader", "The Leader's Mindset", 2)] },
      { keys: ['exécution', 'performance', 'execution'], items: [book("L'âme du projet", "Humans at the Heart of the Project", 3), book("Le modèle symphonique", "The Symphonic Model", 4), book("La gestion des risques décomplexée", "Risk Management in Music", 6)] },
      { keys: ['climat', 'énergie', 'climate', 'energy'], items: [book("Rythme, leadership et ennéagramme", "Rhythm, Leadership and Enneagram", 9), book("L'état d'esprit du leader", "The Leader's Mindset", 2), book("Débloque ton potentiel", "24 Principles to Transform Your Life", 7)] }
    ];

    const lower = axisName.toLowerCase();
    const match = catalog.find(c => c.keys.some(k => lower.includes(k)));
    return match ? match.items : [];
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <div className="text-lg text-gray-600">{t('analysisInProgress')}</div>
        </div>
      </div>
    );
  }

  const criticalAndFragileAxes = analysisResults.filter(
    a => a.classification.level === 'critical' || a.classification.level === 'fragile'
  );

  // ── Vers /pricing ──
  const goToPricing = () => navigate('/pricing');

  // ─────────────────────────── RENDER ───────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">

        {/* Modal upgrade — monté conditionnellement */}
        {showUpgradeModal && (
          <UpgradeModal
            onClose={() => setShowUpgradeModal(false)}
            onGoToPricing={() => { setShowUpgradeModal(false); goToPricing(); }}
          />
        )}

        {/* ══════════ EN-TÊTE ══════════ */}
        <div className="mb-6 flex justify-between items-center">
          <button onClick={() => navigate('/dashboard')} className="text-primary-600 hover:text-primary-700 font-semibold">
            ← {t('backDashboard')}
          </button>

          <DownloadButton canDownload={canDownload} onClick={handleDownloadPDF} className="px-5 py-2">
            {t('downloadReport')}
          </DownloadButton>
        </div>

        {/* ══════════ PAGE DE GARDE ══════════ */}
        <div className="card mb-8 text-center bg-gradient-to-br from-primary-50 to-blue-50">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('diagnosticReport')}</h1>
          <h2 className="text-2xl font-semibold text-primary-700 mb-6">{t('healYourProject')}</h2>

          <div className="grid md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto bg-white rounded-lg p-6">
            <div>
              <p className="text-sm text-gray-600">{t('organizationLabel')}</p>
              <p className="font-semibold text-gray-900">{diagnostic.organization}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('projectLabel')}</p>
              <p className="font-semibold text-gray-900">{diagnostic.projectName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('projectManagerLabel')}</p>
              <p className="font-semibold text-gray-900">{diagnostic.projectManager}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('dateLabel')}</p>
              <p className="font-semibold text-gray-900">
                {new Date(diagnostic.completedAt?.toDate()).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('participantsLabel')}</p>
              <p className="font-semibold text-gray-900">{diagnostic.participants} {t('people')}</p>
            </div>
          </div>
        </div>

        {/* ══════════ 1. RÉSUMÉ EXÉCUTIF ══════════ */}
        {executiveSummary && (
          <div className="card mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">1. {t('executiveSummary')}</h2>

            {/* Score global */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('projectHealthLevel')}</h3>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">{t('globalScore')}</p>
                  <div className="text-6xl font-bold text-primary-600">{globalScore} / 5</div>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600 mb-1">{t('assessment')}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-4xl">{executiveSummary.classification.icon}</span>
                    <span className="text-xl font-bold" style={{ color: executiveSummary.classification.color }}>
                      {executiveSummary.classification.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Constats majeurs */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 {t('majorFindings')}</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <ol className="list-decimal list-inside space-y-2 text-gray-800">
                  {recommendations.slice(0, 3).map((rec, index) => (
                    <li key={index}><strong>{rec.axis}</strong> : {rec.problem}</li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Risques */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🚨 {t('priorityRisks')}</h3>
              <div className="space-y-3">
                {executiveSummary.topRisks.slice(0, 3).map((risk, index) => (
                  <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="font-bold text-red-900">
                      {t('riskNumber', { number: index + 1 })} : {risk.axis} ({t('averageScore')}: {risk.score})
                    </p>
                    {risk.problems && <p className="text-sm text-red-800 mt-1">{risk.problems.risk}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Décisions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">✓ {t('immediateDecisions')}</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <ol className="list-decimal list-inside space-y-2 text-gray-800">
                  {recommendations.slice(0, 3).map((rec, index) => (
                    <li key={index}><strong>{t('decisionNumber', { number: index + 1 })}</strong> : {rec.action}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* ══════════ 2. MÉTHODOLOGIE ══════════ */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-3 border-b">2. {t('methodology')}</h2>
          <ul className="space-y-2 text-gray-700">
            <li>• <strong>{t('toolUsed')} :</strong> {t('structuredQuestionnaire')}</li>
            <li>• <strong>{t('numberOfRespondents')} :</strong> {t('projectManagerLabel')} + {diagnostic.participants - 1} {t('people')}</li>
            <li>• <strong>{t('analysisMode')} :</strong> {t('quantitativeQualitative')}</li>
          </ul>
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">⚠️ {t('methodologyNote')}</p>
          </div>
        </div>

        {/* ══════════ 3. RÉSULTATS CHIFFRÉS ══════════ */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">3. {t('numericalResults')}</h2>

          {/* Tableau */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">{t('axis')}</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">{t('averageScore')}</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">{t('assessment')}</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">{t('color')}</th>
                </tr>
              </thead>
              <tbody>
                {analysisResults.map((axis, index) => (
                  <tr key={axis.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-4 py-3 font-medium">{axis.name}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center font-bold text-primary-600">{axis.score}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center">{axis.classification.label}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center text-2xl">{axis.classification.icon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Histogramme */}
          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-6 text-center">{t('scoreVisualization')}</h3>
            <div className="relative" style={{ height: '400px' }}>
              {/* Grille */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {[5, 4, 3, 2, 1, 0].map(value => (
                  <div key={value} className="flex items-center">
                    <span className="text-xs text-gray-600 w-8 text-right mr-2">{value}</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                ))}
              </div>
              {/* Barres */}
              <div className="absolute inset-0 flex items-end justify-around pt-6 pl-10 pb-12">
                {analysisResults.map(axis => {
                  const heightPct = (parseFloat(axis.score) / 5) * 100;
                  const barColor =
                    axis.classification.level === 'critical' ? 'bg-red-500' :
                    axis.classification.level === 'fragile' ? 'bg-orange-500' :
                    'bg-green-500';
                  return (
                    <div key={axis.id} className="flex flex-col items-center" style={{ width: '14%' }}>
                      <div className="w-full flex flex-col justify-end items-center" style={{ height: '350px' }}>
                        <div className="text-xs font-bold mb-1 text-gray-700">{axis.score}</div>
                        <div className={`w-full rounded-t-lg transition-all ${barColor}`} style={{ height: `${heightPct}%` }}></div>
                      </div>
                      <div className="mt-2 text-xs text-center font-medium text-gray-700 leading-tight" style={{ width: '100%' }}>
                        {axis.name.split(' ').map((word, i) => <div key={i}>{word}</div>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Légende */}
            <div className="mt-6 flex justify-center gap-6 text-sm">
              <div className="flex items-center gap-2"><div className="w-4 h-4 bg-green-500 rounded"></div><span>{t('mastered')}</span></div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 bg-orange-500 rounded"></div><span>{t('fragile')}</span></div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-500 rounded"></div><span>{t('critical')}</span></div>
            </div>
          </div>

          {/* Lecture globale */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t('globalReading')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>{t('criticalAxes')} :</strong> {analysisResults.filter(a => a.classification.level === 'critical').map(a => a.name).join(', ') || t('none')}</li>
              <li>• <strong>{t('fragileAxes')} :</strong> {analysisResults.filter(a => a.classification.level === 'fragile').map(a => a.name).join(', ') || t('none')}</li>
              <li>• <strong>{t('masteredAxes')} :</strong> {analysisResults.filter(a => a.classification.level === 'mastered').map(a => a.name).join(', ') || t('none')}</li>
            </ul>
          </div>
        </div>

        {/* ══════════ 4. ANALYSE DES DYSFONCTIONNEMENTS ══════════ */}
        {criticalAndFragileAxes.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">4. {t('dysfunctionAnalysis')}</h2>
            <p className="text-gray-700 mb-6">{t('dysfunctionIntro')}</p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">{t('axis')}</th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-semibold">{t('averageScore')}</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">{t('observedProblems')}</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">{t('projectImpacts')}</th>
                  </tr>
                </thead>
                <tbody>
                  {criticalAndFragileAxes.map((axis, index) => (
                    <tr key={axis.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-4 py-3 font-medium">{axis.name}</td>
                      <td className="border border-gray-300 px-4 py-3 text-center font-bold text-primary-600">{axis.score}</td>
                      <td className="border border-gray-300 px-4 py-3">{axis.problems?.problem || '-'}</td>
                      <td className="border border-gray-300 px-4 py-3">{axis.problems?.risk || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-3">{t('syntheticAnalysis')}</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• <strong>{t('whatBlocksPerformance')} :</strong> {criticalAndFragileAxes.filter(a => a.classification.level === 'critical').map(a => a.name).join(', ') || t('noMajorBlockage')}</li>
                <li>• <strong>{t('whatGeneratesStress')} :</strong> {criticalAndFragileAxes.find(a => a.id === 'climate')?.problems?.problem || t('climateMastered')}</li>
                <li>• <strong>{t('whatDelaysDecisions')} :</strong> {criticalAndFragileAxes.find(a => a.id === 'governance')?.problems?.problem || t('functionalGovernance')}</li>
              </ul>
            </div>
          </div>
        )}

        {/* ══════════ 5. ANALYSE QUALITATIVE DÉTAILLÉE ══════════ */}
        {criticalAndFragileAxes.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">5. {t('detailedQualitativeAnalysis')}</h2>
            <p className="text-gray-700 mb-4">{t('qualitativeAnalysisIntro')}</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              ⚠️ {t('rootCauseNote')}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('keyQuestion')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('rootCause')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('observableConsequence')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('correctiveAction')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('recommendedResources')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map((rec, index) => {
                    const resources = getResources(rec.axis);

                    const handleResourceClick = () => {
                      if (userData?.subscriptionType !== 'gold') {
                        alert(lang === 'fr'
                          ? 'Cette ressource est réservée aux abonnés Gold. Passez à Gold pour y accéder !'
                          : 'This resource is reserved for Gold subscribers. Upgrade to Gold to access it!');
                        navigate('/pricing');
                      } else {
                        navigate('/library');
                      }
                    };

                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-3 py-2 font-medium">{rec.axis}</td>
                        <td className="border border-gray-300 px-3 py-2 bg-yellow-50 italic text-gray-500">{t('toDefine')}</td>
                        <td className="border border-gray-300 px-3 py-2">{rec.problem}</td>
                        <td className="border border-gray-300 px-3 py-2 font-medium text-primary-700">{rec.action}</td>
                        <td className="border border-gray-300 px-3 py-2 bg-blue-50">
                          <ul className="list-none space-y-1">
                            {resources.map((resource, i) => (
                              <li key={i} className="text-xs">
                                • <button
                                    onClick={handleResourceClick}
                                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                  >
                                    {resource.title}
                                  </button>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══════════ 6. PLAN D'ACTIONS PRIORISÉ ══════════ */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">6. {t('prioritizedActionPlan')}</h2>
          <p className="text-gray-700 mb-4">
            <strong>{lang === 'fr' ? 'Principe' : 'Principle'} :</strong> {t('actionPlanPrinciple')}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
            ⚠️ {t('responsibleDeadlineNote')}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('axis')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('priorityAction')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('responsible')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('deadline')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('successIndicator')}</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.slice(0, 5).map((rec, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-3 py-2 font-medium">{rec.axis}</td>
                    <td className="border border-gray-300 px-3 py-2">{rec.action}</td>
                    <td className="border border-gray-300 px-3 py-2 bg-yellow-50 italic text-gray-500">{t('toDefine')}</td>
                    <td className="border border-gray-300 px-3 py-2 bg-yellow-50 italic text-gray-500">{t('toDefine')}</td>
                    <td className="border border-gray-300 px-3 py-2">{getSuccessIndicator(rec.axis)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══════════ 7. BÉNÉFICES ATTENDUS ══════════ */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">7. {t('expectedBenefits')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              [t('priorityClarification'), t('teamAligned')],
              [t('fasterDecisions'), t('streamlinedProcess')],
              [t('productiveMeetings'), t('optimizedCollectiveTime')],
              [t('betterVisibility'), t('clearSharedProgress')],
              [t('reducedStress'), t('calmWorkEnvironment')],
              [t('restoredPerformance'), t('measurableGains')]
            ].map(([title, desc], i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <h4 className="font-semibold text-gray-900">{title}</h4>
                  <p className="text-sm text-gray-600">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════ 8. RECOMMANDATIONS FINALES ══════════ */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b">8. {t('finalRecommendations')}</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-primary-600 font-bold">•</span>
              <div>
                <strong className="text-gray-900">{t('recommendedFollowUpRhythm')} :</strong>
                <span className="text-gray-700 ml-2">{t('monthly')}</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-primary-600 font-bold">•</span>
              <div>
                <strong className="text-gray-900">{t('nextDiagnosticReview')} :</strong>
                <span className="text-gray-700 ml-2 italic">{t('toFillByPM')}</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-primary-600 font-bold">•</span>
              <div>
                <strong className="text-gray-900">{t('successConditions')} :</strong>
                <ul className="text-gray-700 ml-6 mt-2 space-y-1">
                  <li>- {t('topManagementCommitment')}</li>
                  <li>- {t('pilotingDiscipline')}</li>
                  <li>- {t('decisionClarity')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ 9. CONCLUSION ══════════ */}
        <div className="card bg-gradient-to-r from-primary-600 to-blue-600 text-white mb-8">
          <h2 className="text-2xl font-bold mb-4">9. {t('conclusion')}</h2>
          <p className="text-lg mb-4">{t('conclusionText1')}</p>
          <p className="text-lg">{t('conclusionText2')}</p>
        </div>

        {/* ══════════ BOUTONS BAS DE PAGE ══════════ */}
        <div className="flex gap-4">
          <DownloadButton canDownload={canDownload} onClick={handleDownloadPDF} className="flex-1 py-4 text-lg">
            {t('downloadFullReport')}
          </DownloadButton>
          <button onClick={() => navigate('/dashboard')} className="flex-1 btn-secondary py-4 text-lg">
            {t('backDashboard')}
          </button>
        </div>

        {/* ══════════ BANNER UPGRADE (uniquement pour free) ══════════ */}
        {!canDownload && <UpgradeBanner onGoToPricing={goToPricing} />}
      </div>
    </div>
  );
}

export default DiagnosticResult;
